'use client'

import { useRouter } from '@mntn-dev/app-navigation'
import { route } from '@mntn-dev/app-routing'
import type { UserDomainQueryModel } from '@mntn-dev/domain-types'
import { useRealtimeSubscription } from '@mntn-dev/realtime-updates-service'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { identifyUser } from '~/utils/identify-user.ts'
import { useMe } from './use-me.ts'

/**
 * This hook will identify the current user to highlight.io
 * if the current user hasn't already been identified.
 */
export const useUserIdentifier = () => {
  const router = useRouter()
  const {
    me,
    me: { organization, userId },
  } = useMe()
  const queryClient = useQueryClient()

  // Realtime handling when session is updated. This event fires whenever a user is mutated and is a good place to
  // to trigger a session update to reflect any changes made to the principal, such as team or role changes.
  useRealtimeSubscription(`user:${userId}`, 'session.updated', async () => {
    // The response cookies are merged with the browser so this effectively updates the session.
    const response = await fetch('/api/auth/update', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    })

    if (response.status === 401) {
      // A disabled user manifests as a 401 in '/api/auth/update'. Let's redirect to the login page.
      router.push(route('/auth/logout'))
    } else {
      // Changes to the user's session potentially impact all queries, so we need to invalidate everything.
      // E.g., a user team or permissions change can produce a different list of projects.
      queryClient.invalidateQueries()
    }
  })

  const [lastIdentifiedUser, setLastIdentifiedUser] = useState<
    UserDomainQueryModel | null | undefined
  >(undefined)

  useEffect(() => {
    if (me && me !== lastIdentifiedUser) {
      identifyUser(me)
      setLastIdentifiedUser(me)
    }
  }, [me, lastIdentifiedUser])

  if (!organization) {
    throw new Error('Me is not in an organization.')
  }
}
